import { useMemo, useState } from "react";

/* Memory — ARENA edition. Card backs carry the world's emblem-glow, matches
   pop, the grid scales generously on desktop. Calls onFinish({correct,total}). */
export default function Memory({ module, accent = "#8b5cf6", onFinish }) {
  const pairs = module.pairs || [];
  const total = pairs.length;

  const cards = useMemo(() => {
    const arr = [];
    pairs.forEach((p, i) => { arr.push({ id: `${i}a`, pid: i, t: p.a }); arr.push({ id: `${i}b`, pid: i, t: p.b }); });
    return arr.sort(() => Math.random() - 0.5);
  }, [module.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState({});
  const [mistakes, setMistakes] = useState(0);
  const [busy, setBusy] = useState(false);

  const matchedCount = Object.keys(matched).length;

  const flip = (card) => {
    if (busy || matched[card.pid] || flipped.find((c) => c.id === card.id) || flipped.length === 2) return;
    const nf = [...flipped, card];
    setFlipped(nf);
    if (nf.length === 2) {
      setBusy(true);
      if (nf[0].pid === nf[1].pid) {
        setTimeout(() => {
          const nm = { ...matched, [card.pid]: true };
          setMatched(nm); setFlipped([]); setBusy(false);
          if (Object.keys(nm).length === total) {
            const correct = Math.max(0, total - Math.floor(mistakes / 2));
            setTimeout(() => onFinish({ correct, total }), 450);
          }
        }, 480);
      } else {
        setMistakes((m) => m + 1);
        setTimeout(() => { setFlipped([]); setBusy(false); }, 850);
      }
    }
  };

  const cols = cards.length <= 8 ? "grid-cols-3 sm:grid-cols-4" : "grid-cols-4 sm:grid-cols-6";

  return (
    <div className="mx-auto w-full max-w-3xl">
      <div className="mb-3 flex items-center justify-between">
        <span className="rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-widest" style={{ background: `${accent}1c`, color: accent, border: `1px solid ${accent}40` }}>
          Memory
        </span>
        <div className="flex items-center gap-2">
          <div className="h-2 w-28 overflow-hidden rounded-full bg-white/10">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(matchedCount / total) * 100}%`, background: `linear-gradient(90deg, ${accent}, #22c55e)`, boxShadow: `0 0 10px ${accent}70` }} />
          </div>
          <span className="text-xs font-black text-white/55">{matchedCount}/{total} · {mistakes} Fehler</span>
        </div>
      </div>
      {module.prompt && <p className="mb-4 text-base font-bold text-white/80">{module.prompt}</p>}

      <div className={`grid gap-3 ${cols}`}>
        {cards.map((card) => {
          const isUp = !!matched[card.pid] || !!flipped.find((c) => c.id === card.id);
          const isMatched = !!matched[card.pid];
          return (
            <button key={card.id} onClick={() => flip(card)} className="relative aspect-[3/4] transition-transform hover:scale-[1.03] active:scale-[0.97]" style={{ perspective: 700 }}>
              <div className="relative h-full w-full transition-transform duration-500" style={{ transformStyle: "preserve-3d", transform: isUp ? "rotateY(180deg)" : "rotateY(0deg)" }}>
                {/* back — world-glow emblem */}
                <div className="absolute inset-0 grid place-items-center rounded-2xl text-2xl"
                  style={{
                    backfaceVisibility: "hidden",
                    background: `linear-gradient(150deg, ${accent}20 0%, rgba(12,8,34,0.95) 60%)`,
                    border: `1.5px solid ${accent}35`,
                    boxShadow: `inset 0 0 24px ${accent}12, 0 6px 16px rgba(0,0,0,0.35)`,
                  }}>
                  <span style={{ color: `${accent}90`, filter: `drop-shadow(0 0 10px ${accent}70)` }}>✦</span>
                </div>
                {/* front */}
                <div className="absolute inset-0 grid place-items-center rounded-2xl border p-1.5 text-center text-xs font-black leading-tight sm:text-base"
                  style={{
                    backfaceVisibility: "hidden", transform: "rotateY(180deg)",
                    borderColor: isMatched ? "rgba(34,197,94,0.6)" : `${accent}70`,
                    background: isMatched ? "rgba(34,197,94,0.16)" : `${accent}1e`,
                    color: "#fff",
                    boxShadow: isMatched ? "0 0 20px rgba(34,197,94,0.3)" : `0 0 20px ${accent}30`,
                    ...(isMatched ? { animation: "sx-pop 0.45s both" } : {}),
                  }}>
                  {card.t}
                </div>
              </div>
            </button>
          );
        })}
      </div>
      <p className="mt-5 text-center text-xs font-bold text-white/40">Decke zwei Karten auf und finde die Paare.</p>
    </div>
  );
}
